import React, { useState, useEffect } from 'react';
import { 
  Users, 
  GraduationCap, 
  Calendar,
  Clock,
  RefreshCw,
  UserCheck,
  AlertTriangle,
  Phone,
  Mail,
  ChevronRight
} from 'lucide-react';

const AgentAnalyticsDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [allStudents, setAllStudents] = useState([]);
  const [subAgents, setSubAgents] = useState([]);
  const [scheduledStudents, setScheduledStudents] = useState([]);
  const [overdueStudents, setOverdueStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Get agent ID from localStorage
  const getAgentId = () => {
    const userData = JSON.parse(localStorage.getItem('user')) || {};
    return userData.id || '123';
  };

  const agentId = getAgentId();
  const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/analytics`;

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      const [
        dashboardRes,
        studentsRes,
        subAgentsRes,
        scheduledRes,
        overdueRes
      ] = await Promise.all([
        fetch(`${API_BASE}/${agentId}/dashboard-summary`, { headers }),
        fetch(`${API_BASE}/${agentId}/all-students`, { headers }),
        fetch(`${API_BASE}/${agentId}/sub-agents`, { headers }),
        fetch(`${API_BASE}/${agentId}/scheduled-students?days=7`, { headers }),
        fetch(`${API_BASE}/${agentId}/overdue-students`, { headers })
      ]);

      const dashboardData = await dashboardRes.json();
      const studentsData = await studentsRes.json();
      const subAgentsData = await subAgentsRes.json();
      const scheduledData = await scheduledRes.json();
      const overdueData = await overdueRes.json();

      setDashboardData(dashboardData);
      setAllStudents(studentsData);
      setSubAgents(subAgentsData);
      setScheduledStudents(scheduledData);
      setOverdueStudents(overdueData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const StatCard = ({ icon: Icon, title, value, subtitle, color = "blue" }) => (
    <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${
          color === 'blue' ? 'bg-blue-50' : 
          color === 'green' ? 'bg-green-50' : 
          color === 'orange' ? 'bg-orange-50' : 
          color === 'purple' ? 'bg-purple-50' : 
          'bg-red-50'
        }`}>
          <Icon className={`h-5 w-5 ${
            color === 'blue' ? 'text-blue-600' : 
            color === 'green' ? 'text-green-600' : 
            color === 'orange' ? 'text-orange-600' : 
            color === 'purple' ? 'text-purple-600' : 
            'text-red-600'
          }`} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-600 truncate">{title}</p>
          <p className="text-xl font-bold text-gray-900 truncate">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 truncate">{subtitle}</p>}
        </div>
      </div>
    </div>
  );

  const TabButton = ({ id, label, icon: Icon, active, onClick }) => (
    <button
      onClick={() => onClick(id)}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-colors text-sm ${
        active 
          ? 'bg-blue-100 text-blue-700' 
          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
      }`}
    >
      <Icon className="h-4 w-4" />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );

  const StudentCard = ({ student, isOverdue = false }) => (
    <div className={`bg-white rounded-lg border p-3 hover:shadow-sm transition-shadow ${
      isOverdue ? 'border-red-200 bg-red-50' : 'border-gray-200'
    }`}>
      <div className="flex justify-between items-start mb-2">
        <div className="min-w-0 flex-1">
          <h4 className="font-semibold text-gray-900 text-sm truncate">
            {student.first_name} {student.last_name}
          </h4>
          <p className="text-xs text-gray-600 truncate">{student.programme || 'No Programme'}</p>
          <p className="text-xs text-gray-500 truncate">{student.university || 'No University'}</p>
        </div>
        <div className="text-right ml-2 flex-shrink-0">
          <span className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${
            student.source === 'Direct' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
          }`}>
            {student.source}
          </span>
          {student.student_status && (
            <p className="text-xs text-gray-500 mt-0.5 truncate">{student.student_status}</p>
          )}
        </div>
      </div>
      
      <div className="space-y-1.5">
        <div className="flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
          <span className="text-xs text-gray-600 flex-1 truncate">
            {student.scheduled_date ? new Date(student.scheduled_date).toLocaleDateString() : 'No scheduled date'}
          </span>
          {isOverdue && (
            <span className="text-xs text-red-600 font-medium flex-shrink-0">
              ({student.days_overdue} days)
            </span>
          )}
          {!isOverdue && student.days_until_scheduled !== undefined && (
            <span className="text-xs text-orange-600 font-medium flex-shrink-0">
              (in {student.days_until_scheduled} days)
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-3">
          {student.email && (
            <div className="flex items-center gap-1">
              <Mail className="h-3 w-3 text-gray-400 flex-shrink-0" />
              <span className="text-xs text-gray-600 truncate">{student.email}</span>
            </div>
          )}
          {student.mobile_no && (
            <div className="flex items-center gap-1">
              <Phone className="h-3 w-3 text-gray-400 flex-shrink-0" />
              <span className="text-xs text-gray-600">{student.mobile_no}</span>
            </div>
          )}
        </div>
        
        {student.agent_first_name && student.source === 'Sub-Agent' && (
          <p className="text-xs text-gray-500 truncate">
            Agent: {student.agent_first_name} {student.agent_last_name}
          </p>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5 animate-spin text-blue-600" />
          <span className="text-base font-medium text-gray-700">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-xs border-b">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 py-4">
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-gray-900 truncate">Agent Dashboard</h1>
              <p className="text-sm text-gray-600 truncate">Monitor your students and scheduled appointments</p>
            </div>
            <button 
              onClick={fetchData}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm w-full sm:w-auto justify-center"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4">
        {/* Navigation Tabs */}
        <div className="flex gap-1 mb-6 bg-white p-1 rounded-lg shadow-xs border overflow-x-auto">
          <TabButton 
            id="overview" 
            label="Overview" 
            icon={GraduationCap} 
            active={activeTab === 'overview'} 
            onClick={setActiveTab} 
          />
          <TabButton 
            id="scheduled" 
            label="Scheduled" 
            icon={Calendar} 
            active={activeTab === 'scheduled'} 
            onClick={setActiveTab} 
          />
          <TabButton 
            id="overdue" 
            label="Overdue" 
            icon={AlertTriangle} 
            active={activeTab === 'overdue'} 
            onClick={setActiveTab} 
          />
          <TabButton 
            id="students" 
            label="All Students" 
            icon={Users} 
            active={activeTab === 'students'} 
            onClick={setActiveTab} 
          />
          <TabButton 
            id="subagents" 
            label="Sub-Agents" 
            icon={UserCheck} 
            active={activeTab === 'subagents'} 
            onClick={setActiveTab} 
          />
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard
                icon={GraduationCap}
                title="Total Students"
                value={dashboardData?.overview?.totalStudents || 0}
                subtitle={`${dashboardData?.overview?.directStudents || 0} direct`}
                color="blue"
              />
              <StatCard
                icon={Users}
                title="Sub-Agents"
                value={dashboardData?.overview?.totalSubAgents || 0}
                subtitle="Active agents"
                color="green"
              />
              <StatCard
                icon={Calendar}
                title="Scheduled"
                value={scheduledStudents.length}
                subtitle="This week"
                color="orange"
              />
              <StatCard
                icon={AlertTriangle}
                title="Overdue"
                value={overdueStudents.length}
                subtitle="Past dates"
                color="red"
              />
            </div>

            {/* Quick Views */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Upcoming Scheduled */}
              <div className="bg-white rounded-lg shadow-xs border p-4">
                <h3 className="text-base font-semibold text-gray-900 mb-3">Upcoming Scheduled</h3>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {scheduledStudents.slice(0, 4).map((student, index) => (
                    <StudentCard key={index} student={student} />
                  ))}
                  {scheduledStudents.length === 0 && (
                    <div className="text-center py-6 text-gray-500">
                      <Calendar className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">No scheduled appointments</p>
                    </div>
                  )}
                </div>
                {scheduledStudents.length > 4 && (
                  <button 
                    onClick={() => setActiveTab('scheduled')}
                    className="mt-3 text-blue-600 hover:text-blue-800 text-xs font-medium flex items-center gap-1"
                  >
                    View all {scheduledStudents.length} scheduled
                    <ChevronRight className="h-3 w-3" />
                  </button>
                )}
              </div>

              {/* Overdue */}
              <div className="bg-white rounded-lg shadow-xs border p-4">
                <h3 className="text-base font-semibold text-gray-900 mb-3">Overdue Students</h3>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {overdueStudents.slice(0, 4).map((student, index) => (
                    <StudentCard key={index} student={student} isOverdue={true} />
                  ))}
                  {overdueStudents.length === 0 && (
                    <div className="text-center py-6 text-gray-500">
                      <UserCheck className="h-8 w-8 mx-auto mb-2 text-green-300" />
                      <p className="text-sm">No overdue appointments!</p>
                    </div>
                  )}
                </div>
                {overdueStudents.length > 4 && (
                  <button 
                    onClick={() => setActiveTab('overdue')}
                    className="mt-3 text-red-600 hover:text-red-800 text-xs font-medium flex items-center gap-1"
                  >
                    View all {overdueStudents.length} overdue
                    <ChevronRight className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Scheduled Tab */}
        {activeTab === 'scheduled' && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-xs border p-4">
              <h3 className="text-base font-semibold text-gray-900 mb-3">
                Scheduled Students - {scheduledStudents.length} total
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {scheduledStudents.map((student, index) => (
                  <StudentCard key={index} student={student} />
                ))}
                {scheduledStudents.length === 0 && (
                  <div className="col-span-full text-center py-8 text-gray-500">
                    <Calendar className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">No scheduled appointments</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Overdue Tab */}
        {activeTab === 'overdue' && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-xs border p-4">
              <h3 className="text-base font-semibold text-gray-900 mb-3">
                Overdue Students - {overdueStudents.length} total
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {overdueStudents.map((student, index) => (
                  <StudentCard key={index} student={student} isOverdue={true} />
                ))}
                {overdueStudents.length === 0 && (
                  <div className="col-span-full text-center py-8 text-gray-500">
                    <UserCheck className="h-10 w-10 mx-auto mb-3 text-green-300" />
                    <p className="text-sm">No overdue appointments!</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* All Students Tab */}
        {activeTab === 'students' && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-xs border p-4">
              <h3 className="text-base font-semibold text-gray-900 mb-3">
                All Students - {allStudents.length} total
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {allStudents.map((student, index) => (
                  <StudentCard key={index} student={student} />
                ))}
                {allStudents.length === 0 && (
                  <div className="col-span-full text-center py-8 text-gray-500">
                    <Users className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">No students found</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Sub-Agents Tab */}
        {activeTab === 'subagents' && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-xs border p-4">
              <h3 className="text-base font-semibold text-gray-900 mb-3">
                Sub-Agents - {subAgents.length} total
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sub-Agent
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Users
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Students
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rate
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {subAgents.map((agent, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {agent.first_name} {agent.last_name}
                            </div>
                            <div className="text-xs text-gray-500 truncate">{agent.email}</div>
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                          {agent.total_users || 0}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                          {agent.total_students || 0}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${
                            (agent.conversion_rate || 0) >= 50 ? 'bg-green-100 text-green-800' :
                            (agent.conversion_rate || 0) >= 25 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {agent.conversion_rate || 0}%
                          </span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${
                            agent.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {agent.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {subAgents.length === 0 && (
                      <tr>
                        <td colSpan="5" className="px-3 py-6 text-center text-gray-500">
                          <UserCheck className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                          <p className="text-sm">No sub-agents found</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentAnalyticsDashboard;